import * as yup from 'yup';
import { DocumentNode } from 'graphql';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-form-state-manager';
import { useModal } from 'react-modal-hook';
import { useTranslation } from 'react-i18next';

import {
  EditRegistrationInput,
  GetParticipantDetailsQuery as GetParticipantDetails,
  GetRegistrationSummaryQuery as GetRegistrationSummary,
  ParticipantAttribute,
} from '__generated__/graphql';

import { Update, emailRegex, getServerErrors, sortedParticipantAttributes } from '../../../../common/helpers';
import {
  convertToParticipantFieldEntryInput, getParticipantFieldEntrySchema, prefillFieldEntries,
} from '../../../../common/ParticipantFields/helpers';
import { getParticipantAttributesSchema } from '../../../../common/ParticipantAttributes/helpers';
import { useNotifier } from '../../../../common/Notifications/NotificationProvider';
import AssignmentForm from '../../../../common/Registrations/AssignmentForm';
import ContactPicker, { Participant } from '../../Contacts/ContactPicker';
import EditRegistrationBackendMutation from './EditRegistrationBackendMutation';
import GetParticipantDataQuery from '../GetParticipantDataQuery';
import GetParticipantDetailsQuery from './GetParticipantDetailsQuery';
import GetRegistrationQuery from './GetRegistrationQuery';
import GetRegistrationSummaryQuery from './GetRegistrationSummaryQuery';
import RegistrationInfo from './RegistrationInfo';
import UI from '../../../../common/UI';
import useMutation from '../../../../api/useMutation';
import useProject from '../../../useProject';
import useQuery from '../../../../api/useQuery';
import useValidation from '../../../../common/useValidation';

type Registration = GetRegistrationSummary['registrationSummary']['registrations'][0];
type ParticipantField = Registration['participant_fields'][0];
type ParticipantDetails = GetParticipantDetails['project']['participant'];

const getValues = (
  registration: Registration,
  participantFields: ParticipantField[],
  /** Pass a selectedAssignee to prefill the registration with the participant's data */
  selectedAssignee?: ParticipantDetails | null,
  defaultCountry?: string,
) => {
  const fieldEntryValues = registration.participant_field_entries.map(convertToParticipantFieldEntryInput);

  return {
    id: registration.id,
    participant: {
      email: selectedAssignee?.email || (registration.assigned && registration.participant.email) || null,
      first_name: (selectedAssignee || registration).first_name,
      last_name: (selectedAssignee || registration).last_name,
    },
    details: {
      street: (selectedAssignee || registration).street,
      house_number: (selectedAssignee || registration).house_number,
      zip_code: (selectedAssignee || registration).zip_code,
      city: (selectedAssignee || registration).city,
      extra_address_line: (selectedAssignee || registration).extra_address_line,
      country: (selectedAssignee || registration).country || defaultCountry,
      date_of_birth: (selectedAssignee || registration).date_of_birth,
      gender: (selectedAssignee || registration).gender,
      nationality: (selectedAssignee || registration).nationality || defaultCountry,
      phone: (selectedAssignee || registration).phone,
      emergency_phone: (selectedAssignee || registration).emergency_phone,
    },
    fields: prefillFieldEntries(fieldEntryValues, participantFields),
  };
};

const getRegistrationSchema = (
  attributes: ParticipantAttribute[],
  participantFields: ParticipantField[],
) => yup.object({
  participant: yup.object({
    email: yup.string().required().matches(emailRegex, { name: 'email' }),
    first_name: yup.string().ensure().required(),
    last_name: yup.string().ensure().required(),
  }),
  details: getParticipantAttributesSchema(attributes, false).nullable().default(null),
  fields: yup.array(getParticipantFieldEntrySchema(participantFields)),
});

export interface EditRegistrationFormProps {
  registration: Registration;
  onSuccess: () => void;
  refetchQueries?: DocumentNode[];
}

export const EditRegistrationForm = ({
  registration, onSuccess, refetchQueries = [GetParticipantDataQuery, GetRegistrationSummaryQuery],
}: EditRegistrationFormProps) => {
  const { t } = useTranslation();
  const notifier = useNotifier();
  const project = useProject();

  const [selectedAssignee, setSelectedAssignee] = useState<Participant | null>(null);
  const [assigneeId, setAssigneeId] = useState<string | null>(null);
  const [sendNotifications, setSendNotifications] = useState<boolean>(false);

  // Attributes are editable when they are required or not empty.
  const editableAttributes = sortedParticipantAttributes.filter((attribute) => {
    if (registration.required_attributes.includes(attribute)) {
      return true;
    }

    if (attribute === ParticipantAttribute.address) {
      return !!registration.street;
    }

    if (attribute === ParticipantAttribute.date_of_birth) {
      return !!registration.date_of_birth;
    }

    if (attribute === ParticipantAttribute.gender) {
      return !!registration.gender;
    }

    if (attribute === ParticipantAttribute.nationality) {
      return !!registration.nationality;
    }

    if (attribute === ParticipantAttribute.phone) {
      return !!registration.phone;
    }

    if (attribute === ParticipantAttribute.emergency_phone) {
      return !!registration.emergency_phone;
    }

    return false;
  });

  // Make all fields optional in the interface and validation.
  const participantFields = registration.participant_fields.map((field) => ({
    ...field,
    required: false,
  }));

  const form = useForm<EditRegistrationInput>({
    values: getValues(registration, participantFields, null, project.organisation_country),
  });

  const { loading: fetching } = useQuery(
    GetParticipantDetailsQuery, {
      variables: {
        project_id: project.id,
        participant_id: selectedAssignee?.id,
      },
      skip: !selectedAssignee?.id,
      onCompleted: ({ project: { participant } }) => {
        form.set(getValues(registration, participantFields, participant, project.organisation_country));

        // Rerender form.
        setAssigneeId(participant.id);
      },
    },
  );

  const selectAssignee = (assignee: Participant | null) => {
    setSelectedAssignee(assignee);
  };

  /** Indicates whether a new assignee has been selected or the current assignee has been deselected */
  const emailChanged = form.values.participant.email
    && registration.participant.email !== form.values.participant.email;

  const [
    editRegistration,
    { error, loading: submitting },
  ] = useMutation(
    EditRegistrationBackendMutation,
    {
      variables: {
        input: {
          id: registration.id,
          send_notifications: emailChanged && sendNotifications,
          participant: {
            email: form.values.participant.email,
            first_name: form.values.participant.first_name,
            last_name: form.values.participant.last_name,
          },
          details: {
            ...editableAttributes.includes(ParticipantAttribute.address) ? {
              street: form.values.details.street,
              house_number: form.values.details.house_number,
              zip_code: form.values.details.zip_code,
              city: form.values.details.city,
              extra_address_line: form.values.details.extra_address_line,
              country: form.values.details.country,
            } : {},
            ...editableAttributes.includes(ParticipantAttribute.date_of_birth) ? {
              date_of_birth: form.values.details.date_of_birth,
            } : {},
            ...editableAttributes.includes(ParticipantAttribute.gender) ? {
              gender: form.values.details.gender,
            } : {},
            ...editableAttributes.includes(ParticipantAttribute.nationality) ? {
              nationality: form.values.details.nationality,
            } : {},
            ...editableAttributes.includes(ParticipantAttribute.phone) ? {
              phone: form.values.details.phone,
            } : {},
            ...editableAttributes.includes(ParticipantAttribute.emergency_phone) ? {
              emergency_phone: form.values.details.emergency_phone,
            } : {},
          },
          fields: form.values.fields,
        },
      },
      refetchQueries,
      awaitRefetchQueries: true,
      onCompleted: () => {
        notifier.success(t('notifications.registration_saved'));
        onSuccess();
      },
    },
  );

  const loading = submitting || fetching;

  const handleChange = (update: Update<EditRegistrationInput>) => {
    form.set((registration) => ({ ...registration, ...update(registration) }));
  };

  const { valid, errors, updateCustomErrors, updateValidating } = useValidation({
    schema: getRegistrationSchema(editableAttributes, participantFields),
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  return (
    <UI.Form onSubmit={() => editRegistration()}>
      <UI.FormGrid>
        <UI.Div>
          <UI.H4 sc={{ mb: 2 }}>
            {registration.assigned ? t('edit_registration') : t('assign_ticket')}
          </UI.H4>
          <UI.Paragraph sc={{ mb: 0 }}>
            {registration.assigned ? t('edit_assignee_description') : t('assign_ticket_description')}
          </UI.Paragraph>
        </UI.Div>

        <RegistrationInfo registration={registration} />

        <UI.ServerErrors error={error} />

        <UI.FormGrid>
          <UI.InputGroup>
            <UI.InputLabel>
              {t('common:assignment_form.copy_details_from')}
            </UI.InputLabel>

            <ContactPicker
              participant={selectedAssignee}
              onChange={(participant) => selectAssignee(participant)}
              nullable
              disabled={fetching}
            />
          </UI.InputGroup>

          <AssignmentForm
            registration={form.values}
            onChange={handleChange}
            onBlur={(field) => form.setTouched(field, true)}
            touched={form.touched}
            registrationAttributes={editableAttributes}
            reassignable
            allowAssignLater={false}
            required={false}
            errors={errors}
            enabledParticipantFields={participantFields}
            existingFieldEntries={registration.participant_field_entries}
            updateCustomErrors={updateCustomErrors}
            updateValidating={updateValidating}
            key={assigneeId}
          />
        </UI.FormGrid>

        {emailChanged && (
          <>
            <UI.HR />
            <UI.InputGroup>
              <UI.InputLabel>
                {t('send_confirmation')}
              </UI.InputLabel>
              <UI.Checkbox
                checked={sendNotifications}
                onChange={(event) => setSendNotifications(event.target.checked)}
              >
                {t('send_reassign_ticket_notification')}
              </UI.Checkbox>
            </UI.InputGroup>
          </>
        )}

        <UI.HR />
        <UI.Div sc={{ textAlign: 'right' }}>
          <UI.Button
            type="submit"
            disabled={!form.changed() || !valid || loading}
            sc={{ brand: 'secondary', width: ['100%', 'auto'], loading }}
          >
            {t('confirm')}
          </UI.Button>
        </UI.Div>
      </UI.FormGrid>
    </UI.Form>
  );
};

interface UseEditRegistrationModalProps {
  registrationId: string;
  refetchQueries?: DocumentNode[];
}

const useEditRegistrationModal = ({
  registrationId, refetchQueries,
}: UseEditRegistrationModalProps) => {
  /**
   * Used to prevent state updates on an unmounted component.
   * When a registration is assigned away, the <RegistrationDetails /> component is unmounted.
   * If we don't check for this, React may throw an error.
   */
  const mountedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  });

  const [open, setOpen] = useState(false);

  const { data } = useQuery(
    GetRegistrationQuery,
    {
      variables: {
        id: registrationId,
      },
      skip: !open,
    },
  );

  const registration = data?.registration;

  const [doOpenModal, closeModal] = useModal(() => {
    const handleSuccess = () => {
      if (mountedRef.current) {
        closeModal();
      }
    };

    return (
      <UI.Modal maxWidth={600} close={closeModal}>
        {!registration && <UI.Loader />}

        {registration && (
          <EditRegistrationForm
            registration={registration}
            refetchQueries={refetchQueries}
            onSuccess={handleSuccess}
          />
        )}
      </UI.Modal>
    );
  }, [registration, refetchQueries]);

  const openModal = () => {
    setOpen(true);
    doOpenModal();
  };

  return [openModal, closeModal];
};

export default useEditRegistrationModal;
